import {
  LIVE_VIEW_ACTIVITY_TYPES,
  type ActivityItem,
  type LiveViewActivity
} from "./types";

export const isLiveViewActivity = (
  activity: ActivityItem
): activity is LiveViewActivity =>
  (LIVE_VIEW_ACTIVITY_TYPES as readonly string[]).includes(
    activity.activity_type
  );

/** "45 s", "4 min", "1 h 5 min" */
export const formatLiveViewDuration = (seconds: number) => {
  if (seconds < 60) return `${Math.max(1, Math.round(seconds))} s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
};

/**
 * The log line for a live-view event. The actor is the partner user who
 * watched, or "Boom" for Boom staff (the API never sends their name).
 */
export const liveViewActivityText = (
  activity: LiveViewActivity
): { title: string; detail?: string } => {
  const actor = activity.author?.name || "Someone from your team";
  const applicant = activity.details?.applicant_name || "the applicant";
  const seconds = activity.details?.duration_seconds ?? 0;

  switch (activity.activity_type) {
    case "live_view_requested":
      return { title: `${actor} asked to view ${applicant}'s screen` };
    case "live_view_started":
      return { title: `${applicant} shared their screen with ${actor}` };
    case "live_view_declined":
      return {
        title: `${applicant} declined to share their screen with ${actor}`
      };
    case "live_view_expired":
      return {
        title: `Request to view ${applicant}'s screen expired`,
        detail: `Requested by ${actor}`
      };
    case "live_view_ended":
      return seconds > 0
        ? {
            title: `Live view ended after ${formatLiveViewDuration(seconds)}`,
            detail: `${actor} viewed ${applicant}'s screen`
          }
        : {
            title: "Live view ended",
            detail: `Stopped before ${applicant} shared their screen`
          };
    default:
      return { title: "Live view" };
  }
};
